import React, { useEffect, useId, useState } from "react";
import toast from "react-hot-toast";
import { X, Play, AlertTriangle } from "lucide-react";

import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogClose,
} from "@/components/ui/Modal";
import { ButtonShadcn as Button } from "@/components/ui/Button";
import { Callout } from "@/components/ui/Callout";
import { Checkbox } from "@/components/ui/Checkbox";
import { Input } from "@/components/ui/Input";
import Skeleton from "@/components/shared/Skeleton";
import { PlatformService } from "@/services/platformService";
import { apiErrorMessageFormat } from "@/utils/formErrorUtils";

import { CONFIRM_INPUT_CLASS } from "../constants";
import CodeBlock from "./CodeBlock";
import ErrorNote from "./ErrorNote";

/**
 * RLS Apply / Preview modal.
 *
 * `row` shape: the RlsDatabaseStatus payload the dashboard already has.
 * A synthetic `{ key: 'all', isSyntheticAll: true, label: 'All ...' }`
 * lets the Apply-all button reuse this modal.
 *
 * Preview always runs first (auto on open when previewOnly=true, or when
 * the user clicks Preview from the Apply form). Apply is disabled until
 * the SuperAdmin types the DB label into the confirmation field so a
 * miss-click can't drift the fleet.
 */
const RlsApplyModal = ({
    row,
    previewOnly = false,
    rlsCodeVersion,
    onClose,
    onCompleted,
}) => {
    const [previewLoading, setPreviewLoading] = useState(false);
    const [previewStatements, setPreviewStatements] = useState([]);
    const [confirmText, setConfirmText] = useState("");
    const [applying, setApplying] = useState(false);
    const [error, setError] = useState(null);
    const confirmId = useId();
    // Stamp-only skips all DDL and just writes rls_meta.version. Used
    // when the target's policies are already known to match the current
    // script (e.g. cosmetic version bumps that don't touch the policy
    // shape, or a DB set up via the historical setup_rls_role.sql).
    // Always safe — zero lock contention — but the SuperAdmin has to
    // opt in because it means "trust me, the policies already match".
    const [stampOnly, setStampOnly] = useState(false);

    // Backend accepts 'platform' | 'all' | 'tenant:<uuid>'. For dedicated
    // rows the row.key is the raw tenant UUID, so we must prefix it —
    // otherwise the endpoint returns 400 "invalid target". The platform
    // row's key IS the literal string "platform" so it needs no prefix.
    const target = row?.isSyntheticAll
        ? "all"
        : row?.isPlatform || row?.key === "platform"
        ? "platform"
        : `tenant:${row?.tenantId ?? row?.key ?? ""}`;
    const isAll = row?.isSyntheticAll || target === "all";
    // The confirmation gate — we ask them to type the exact label. For
    // `all` it's the literal string "APPLY ALL" so it's harder to fat-finger.
    const expectedConfirm = isAll ? "APPLY ALL" : row?.tenantSlug || "PLATFORM DB";
    const versionLabel = rlsCodeVersion == null ? "the code version" : `v${rlsCodeVersion}`;

    useEffect(() => {
        // Autoload the preview when the modal opens for a single target.
        // Preview isn't meaningful for the fleet-wide 'all' target because
        // the SQL would differ per DB — skip it.
        if (!row || isAll) return;
        let cancelled = false;
        (async () => {
            try {
                setPreviewLoading(true);
                setError(null);
                const data = await PlatformService.previewRls({ target });
                if (cancelled) return;
                setPreviewStatements(data?.statements ?? []);
            } catch (err) {
                if (!cancelled) setError(apiErrorMessageFormat(err));
            } finally {
                if (!cancelled) setPreviewLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [row, target, isAll]);

    const canApply =
        !previewOnly && confirmText.trim() === expectedConfirm && !applying;

    const onApplyClick = async () => {
        if (!canApply) return;
        try {
            setApplying(true);
            setError(null);
            const data = await PlatformService.applyRls({ target, stampOnly });
            const succeeded = (data?.results ?? []).filter((r) => r.success).length;
            const failed = (data?.results ?? []).filter((r) => !r.success).length;
            if (failed > 0) {
                toast.error(
                    `${failed} database${failed > 1 ? "s" : ""} failed. Open Migration history on that row for the reason.`
                );
            } else {
                toast.success(
                    `RLS applied to ${succeeded} database${succeeded === 1 ? "" : "s"}.`
                );
            }
            onCompleted?.();
        } catch (err) {
            setError(apiErrorMessageFormat(err));
        } finally {
            setApplying(false);
        }
    };

    return (
        <Dialog open={!!row} onOpenChange={(o) => !o && !applying && onClose?.()}>
            <DialogContent className="w-[340px] md:w-[720px] max-h-[85vh] flex flex-col">
                <DialogHeader className="relative px-5 py-3 border-b border-border-primary shrink-0">
                    <DialogTitle className="text-[15px] font-semibold text-text-primary">
                        {previewOnly ? "Preview row security" : "Apply row security"}
                    </DialogTitle>
                    <DialogDescription className="text-[12px] text-text-secondary mt-0.5">
                        Target: <span className="font-medium">{row?.label}</span> ·
                        applying {versionLabel}
                    </DialogDescription>
                    <DialogClose
                        disabled={applying}
                        aria-label="Close"
                        className="absolute right-5 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full bg-background-icon/40 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <X size={16} strokeWidth={3} aria-hidden="true" />
                    </DialogClose>
                </DialogHeader>

                <div className="flex-1 overflow-y-auto custom-scrollbar px-5 py-4 space-y-4">
                    {/* Preview section */}
                    {isAll ? (
                        <Callout tone="warning" icon={AlertTriangle} size="xs">
                            <span className="font-medium block mb-0.5">
                                No preview for a fleet-wide apply
                            </span>
                            Every database gets the same script, rendered against its
                            own app role. Preview a single database from its row to see
                            the exact statements.
                        </Callout>
                    ) : (
                        <div>
                            <div className="text-xs font-medium text-text-secondary mb-2">
                                SQL that Apply will run
                            </div>
                            <CodeBlock>
                                {previewLoading ? (
                                    <Skeleton className="h-24 w-full" />
                                ) : previewStatements.length === 0 ? (
                                    <span className="text-text-muted">
                                        No preview available.
                                    </span>
                                ) : (
                                    previewStatements.map((stmt, i) => (
                                        <pre key={i} className="whitespace-pre-wrap mb-2">
                                            {stmt};
                                        </pre>
                                    ))
                                )}
                            </CodeBlock>
                        </div>
                    )}

                    {/* Stamp-only opt-in (hidden in preview-only mode).
                        When checked, the backend skips all DDL and just writes
                        the rls_meta version. Zero locks — always succeeds. Only
                        tick it when the policies are already correct: a cosmetic
                        version bump, or a DB set up by hand. */}
                    {!previewOnly && (
                        <label className="flex items-start gap-2 rounded-md border border-border-primary bg-background-form/30 px-3 py-2 text-xs cursor-pointer">
                            <Checkbox
                                checked={stampOnly}
                                onCheckedChange={(checked) => setStampOnly(checked === true)}
                                disabled={applying}
                                className="mt-0.5"
                            />
                            <div>
                                <div className="font-medium text-text-primary">
                                    Record the version without re-applying the policies
                                </div>
                                <div className="text-text-secondary mt-0.5">
                                    Tick this only when the policies on this database
                                    already match {versionLabel}. The server writes{" "}
                                    <span className="font-mono">rls_meta.version</span>{" "}
                                    and runs no DDL — no locks, no downtime. Use it for a
                                    cosmetic version bump, or a database set up through{" "}
                                    <span className="font-mono">setup_rls_role.sql</span>.
                                </div>
                            </div>
                        </label>
                    )}

                    {/* Confirmation section (hidden in preview-only mode) */}
                    {!previewOnly && (
                        <div>
                            <label
                                htmlFor={confirmId}
                                className="block text-xs text-text-secondary mb-1"
                            >
                                Type{" "}
                                <span className="font-mono font-semibold text-text-primary">
                                    {expectedConfirm}
                                </span>{" "}
                                to confirm
                            </label>
                            <Input
                                id={confirmId}
                                type="text"
                                value={confirmText}
                                onChange={(e) => setConfirmText(e.target.value)}
                                disabled={applying}
                                className={CONFIRM_INPUT_CLASS}
                                placeholder={expectedConfirm}
                                autoComplete="off"
                            />
                        </div>
                    )}

                    {error && <ErrorNote>{error}</ErrorNote>}
                </div>

                <div className="border-t border-border-primary px-5 py-3 flex justify-end gap-2">
                    <DialogClose
                        disabled={applying}
                        render={
                            <Button variant="outline" type="button" disabled={applying}>
                                {previewOnly ? "Close" : "Cancel"}
                            </Button>
                        }
                    />
                    {!previewOnly && (
                        <Button
                            onClick={onApplyClick}
                            disabled={!canApply}
                            className="bg-button-secondary text-text-base"
                        >
                            <Play size={14} className="mr-1" aria-hidden="true" />
                            {applying
                                ? stampOnly
                                    ? "Recording…"
                                    : "Applying…"
                                : stampOnly
                                ? "Record the version"
                                : "Apply"}
                        </Button>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default RlsApplyModal;
